import { z } from "zod"

import { PROJECT_TYPES } from "./project-types"
import type { DashboardStageFilter, ProjectTypeTab } from "./dashboard-filters"

const projectTypeEnum = z.enum(PROJECT_TYPES)

/** Validated `/dashboard` filter params — selected board, stage, project type. */
export const dashboardFiltersSearchSchema = z.object({
  /** Board shown in the table; absent means "the first board", resolved once boards load. */
  board: z.coerce.number().int().positive().optional().catch(undefined),
  stage: z.string().optional(),
  project_type: z.string().optional(),
})

export type DashboardFiltersSearch = z.infer<typeof dashboardFiltersSearchSchema>

const _projectTypes = new Set<string>(PROJECT_TYPES)

/**
 * Cast raw URL strings to their filter types, falling back to "all" for unknown values. Stage names
 * are account-configured, so pass `validStages` once the option list has loaded; while it is still
 * loading the raw value passes through untouched so a valid `?stage=` is not dropped on first paint.
 */
export function coerceDashboardFilters(
  search: DashboardFiltersSearch,
  validStages?: ReadonlySet<string>,
): {
  stage: DashboardStageFilter
  project_type: ProjectTypeTab
} {
  const rawStage = search.stage ?? ""
  const stage = rawStage === "" || (validStages && !validStages.has(rawStage)) ? "all" : rawStage
  return {
    stage: stage as DashboardStageFilter,
    project_type: (_projectTypes.has(search.project_type ?? "") ? search.project_type : "all") as ProjectTypeTab,
  }
}

/** Validated `/dashboard` search params (table state in the URL). */
export const projectsDashboardSearchSchema = z.object({
  q: z.preprocess(
    (val) => (val === "" || val === null || val === undefined ? undefined : val),
    z.string().min(1).max(200).optional(),
  ),
  limit: z.coerce.number().int().min(1).max(200).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  project_type: projectTypeEnum.optional(),
})

export type ProjectsDashboardSearch = z.infer<typeof projectsDashboardSearchSchema>

/** Use for `Link` / `redirect` to `/dashboard` so validated search params are satisfied. */
export const defaultDashboardSearch: ProjectsDashboardSearch =
  projectsDashboardSearchSchema.parse({})

/** Validated `/settings/projects/archived` search params. */
export const projectsArchivedSearchSchema = z.object({
  q: z.preprocess(
    (val) => (val === "" || val === null || val === undefined ? undefined : val),
    z.string().min(1).max(200).optional(),
  ),
  limit: z.coerce.number().int().min(1).max(200).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z.string().default("archived_at.desc"),
  project_type: projectTypeEnum.optional(),
})

export type ProjectsArchivedSearch = z.infer<typeof projectsArchivedSearchSchema>

export const defaultArchivedSearch: ProjectsArchivedSearch = projectsArchivedSearchSchema.parse({})
